/** Base entity class providing a sprite, shared event emitter, and component lifecycle management. */
import Phaser from 'phaser';
import { Component } from '../components/Component';

type EntitySprite = Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;

/** Composable game object: wraps a Phaser sprite and routes updates to attached components. */
export class Entity {
  protected scene: Phaser.Scene;
  sprite: EntitySprite;
  events: Phaser.Events.EventEmitter;
  private components: Map<string, Component> = new Map();

  constructor(scene: Phaser.Scene, sprite: EntitySprite) {
    this.scene = scene;
    this.sprite = sprite;
    this.events = new Phaser.Events.EventEmitter();
  }

  /** Attach a named component and return this entity for chaining. */
  addComponent(name: string, component: Component): this {
    this.components.set(name, component);
    return this;
  }

  /** Retrieve an attached component by name, cast to the given type. */
  getComponent<T extends Component>(name: string): T | undefined {
    return this.components.get(name) as T | undefined;
  }

  /** Tick all components and update depth for 3/4-perspective sorting. */
  update(delta: number): void {
    for (const component of this.components.values()) {
      component.update(delta);
    }

    // 3/4 perspective depth sorting: use body bottom (feet position) so
    // entities sort by where they stand on the ground, not sprite center.
    const body = this.sprite.body as Phaser.Physics.Arcade.Body;
    this.sprite.setDepth(body ? body.bottom : this.sprite.y);
  }

  /** Destroy all components, the event emitter, and the sprite. */
  destroy(): void {
    for (const component of this.components.values()) {
      component.destroy();
    }
    this.components.clear();
    this.events.destroy();
    this.sprite.destroy();
  }
}
